import React, { useState, useEffect } from 'react';
import { Modal, Stack, TextInput, Button, Group, Textarea } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { getErrorMessage } from '../../utils/api';
import { createEnterKeyHandler } from '../../utils/enterKeyHandler';
import { createLabelWithLink } from '../../utils/labelWithLink';
import { Grade } from '../../types';
import { useGenerateGradeDescription } from '../../hooks/useApi';

interface GradeEditModalProps {
  opened: boolean;
  onClose: () => void;
  grade: Grade | null; // null для создания, объект для редактирования
  onGradeCreated?: (gradeId: string, gradeData: Grade) => void;
  onGradeUpdated?: (gradeId: string, gradeData: Grade) => void;
}

export const GradeEditModal: React.FC<GradeEditModalProps> = ({
  opened,
  onClose,
  grade,
  onGradeCreated,
  onGradeUpdated,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    qualification: '',
    position: '',
  });
  const [isGenerating, setIsGenerating] = useState(false);

  const isEditMode = !!grade;

  // Инициализация формы при открытии модалки
  useEffect(() => {
    if (opened) {
      if (grade) {
        // Режим редактирования - заполняем форму данными грейда
        setFormData({
          title: grade.title,
          description: grade.description,
          qualification: grade.qualification,
          position: grade.position,
        });
      } else {
        // Режим создания - очищаем форму
        setFormData({
          title: '',
          description: '',
          qualification: '',
          position: '',
        });
      }
    }
  }, [opened, grade]);

  const generateDescription = useGenerateGradeDescription();

  const createGradeMutation = useMutation({
    mutationFn: async (data: typeof formData) => api('/grades', { method: 'POST', body: data }),
    onSuccess: (data) => {
      showToast({
        title: t('messages.created', { item: t('grades.title') }),
        message: '',
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['grades'] });
      if (onGradeCreated) {
        onGradeCreated(data._id, data);
      }
      handleClose();
    },
    onError: (error) => {
      const message = getErrorMessage(
        error,
        t('messages.errorCreating', { item: t('grades.title') }),
      );
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  const updateGradeMutation = useMutation({
    mutationFn: async (data: typeof formData) =>
      api(`/grades/${grade?._id}`, { method: 'PATCH', body: data }),
    onSuccess: (data) => {
      showToast({
        title: t('messages.updated', { item: t('grades.title') }),
        message: '',
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['grades'] });
      if (onGradeUpdated) {
        onGradeUpdated(data._id, data);
      }
      handleClose();
    },
    onError: (error) => {
      const message = getErrorMessage(
        error,
        t('messages.errorUpdating', { item: t('grades.title') }),
      );
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  const handleClose = () => {
    setFormData({ title: '', description: '', qualification: '', position: '' });
    onClose();
  };

  const handleSave = () => {
    if (
      !formData.title.trim() ||
      !formData.description.trim() ||
      !formData.qualification.trim() ||
      !formData.position.trim()
    ) {
      showToast({ title: t('common.error'), message: t('messages.validationError'), color: 'red' });
      return;
    }

    if (isEditMode) {
      updateGradeMutation.mutate(formData);
    } else {
      createGradeMutation.mutate(formData);
    }
  };

  const handleKeyDown = createEnterKeyHandler({ onSubmit: handleSave });

  const isLoading = createGradeMutation.isPending || updateGradeMutation.isPending;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEditMode ? t('grades.editGrade') : t('grades.addGrade')}
      size="md"
    >
      <Stack>
        <TextInput
          label={t('grades.name')}
          placeholder={t('grades.name')}
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          autoFocus
          onKeyDown={handleKeyDown}
        />
        <Textarea
          placeholder={t('grades.description')}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          onKeyDown={handleKeyDown}
          minRows={3}
          rows={4}
          {...createLabelWithLink({
            labelText: t('grades.description'),
            linkText: isGenerating ? t('common.generating') : t('common.generate'),
            onLinkClick: async (e) => {
              e.preventDefault();

              if (!formData.title.trim()) {
                showToast({
                  title: t('common.error'),
                  message: t('grades.generateDescriptionError'),
                  color: 'red',
                });
                return;
              }

              try {
                setIsGenerating(true);

                const result = await generateDescription.mutateAsync({
                  title: formData.title,
                  qualification: formData.qualification,
                  position: formData.position,
                });

                if (result.success && result.description) {
                  setFormData({ ...formData, description: result.description });
                  showToast({
                    title: t('common.success'),
                    message: t('grades.descriptionGenerated'),
                    color: 'teal',
                  });
                }
              } catch (error: unknown) {
                console.error('Ошибка генерации описания:', error);
                showToast({
                  title: t('common.error'),
                  message:
                    error instanceof Error ? error.message : t('grades.generateDescriptionError'),
                  color: 'red',
                });
              } finally {
                setIsGenerating(false);
              }
            },
          })}
        />
        <TextInput
          label={t('grades.qualification')}
          placeholder={t('grades.qualification')}
          value={formData.qualification}
          onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
          onKeyDown={handleKeyDown}
        />
        <TextInput
          label={t('grades.position')}
          placeholder={t('grades.position')}
          value={formData.position}
          onChange={(e) => setFormData({ ...formData, position: e.target.value })}
          onKeyDown={handleKeyDown}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={handleClose} color="gray">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} color="teal" loading={isLoading}>
            {isEditMode ? t('common.save') : t('common.add')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
